import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import { EXCEPTIONS_QUERY_KEY } from '@/features/exceptions/useExceptions';
import { NOTIFICATIONS_QUERY_KEY } from '@/features/notifications/useNotifications';
import { FIRST_VALUATION_QUERY_KEY } from '@/features/valuation/first-valuation/useFirstValuation';
import {
  addDismantlingWorkEntry,
  cancelDismantlingSession,
  confirmDismantlingIntermediate,
  confirmDismantlingSession,
  createDismantlingSession,
  fetchCancelledDismantlingSessions,
  fetchDismantlingPackages,
  fetchDismantlingSession,
  previewDismantlingSessionCancel,
  restoreCancelledDismantlingSession,
  updateDismantlingSession,
  voidDismantlingIntermediate,
  voidDismantlingWorkEntry,
  type CancelledDismantlingSessionListResult,
  type DismantlingListMeta,
  type DismantlingPackageFilters,
} from './api';
import type {
  AddDismantlingWorkEntryInput,
  CreateDismantlingSessionInput,
  DismantlingIntermediateDto,
  DismantlingCancelPreview,
  DismantlingCancelResult,
  DismantlingPackageDto,
  DismantlingSessionDetailDto,
  UpdateDismantlingSessionInput,
} from './types';

const PKG_KEY = 'dismantling-packages' as const;
const SESSION_KEY = 'dismantling-sessions' as const;

export const invalidateDismantlingChainQueries = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: [PKG_KEY] });
  void qc.invalidateQueries({ queryKey: [SESSION_KEY] });
  invalidateDismantlingDownstreamQueries(qc);
};

const invalidateDismantlingDownstreamQueries = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: ['inbound-lots'] });
  void qc.invalidateQueries({ queryKey: ['dispatch-requests'] });
  void qc.invalidateQueries({ queryKey: ['sorting-packages'] });
  void qc.invalidateQueries({ queryKey: ['sorting-decisions'] });
  void qc.invalidateQueries({ queryKey: ['inventory'] });
  void qc.invalidateQueries({ queryKey: ['evidence'] });
  void qc.invalidateQueries({ queryKey: ['file-library'] });
  void qc.invalidateQueries({ queryKey: ['supplier-portal'] });
  void qc.invalidateQueries({ queryKey: ['mass-balance'] });
  void qc.invalidateQueries({ queryKey: [FIRST_VALUATION_QUERY_KEY] });
  void qc.invalidateQueries({ queryKey: [EXCEPTIONS_QUERY_KEY] });
  void qc.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
  void qc.invalidateQueries({ queryKey: ['correction-requests', 'targets'] });
  void qc.invalidateQueries({ queryKey: ['audit-package', 'precheck'] });
  void qc.invalidateQueries({ queryKey: ['secondary-process-packages'] });
  void qc.invalidateQueries({ queryKey: ['disassembly-batches', 'inbound-packages'] });
  void qc.invalidateQueries({ queryKey: ['monthly-material-valuation'] });
  void qc.invalidateQueries({ queryKey: ['getit-purchase-price'] });
  void qc.invalidateQueries({ queryKey: ['monthly-batches'] });
  void qc.invalidateQueries({ queryKey: ['monthly-reports'] });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
};

export const useDismantlingPackages = (
  batchId: string | undefined,
  filters: DismantlingPackageFilters = {},
) => {
  return useQuery<{ data: DismantlingPackageDto[]; meta: DismantlingListMeta }, ApiClientError>({
    queryKey: [PKG_KEY, 'list', batchId, filters],
    queryFn: () => fetchDismantlingPackages(batchId!, filters),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

/**
 * Resolves an explicit set of pallets server-side, for the 仕分け → 解体
 * hand-off deep link. The list query only ever holds one filtered, paginated
 * window, so the requested pallets may well not be in it.
 */
export const useDismantlingPackagesByIds = (batchId: string | undefined, packageIds: string[]) => {
  const inbound_package_ids = packageIds.join(',');
  return useQuery<{ data: DismantlingPackageDto[]; meta: DismantlingListMeta }, ApiClientError>({
    queryKey: [PKG_KEY, 'by-ids', batchId, inbound_package_ids],
    queryFn: () => fetchDismantlingPackages(batchId!, { inbound_package_ids }),
    enabled: !!batchId && packageIds.length > 0,
  });
};

const ADD_CANDIDATE_PAGE_SIZE = 200; // the backend's per_page ceiling

/** Every pallet of the batch not yet in a session, for the "add a pallet" picker. */
export const useDismantlingAddCandidates = (batchId: string | undefined, enabled: boolean) => {
  return useQuery<DismantlingPackageDto[], ApiClientError>({
    queryKey: [PKG_KEY, 'add-candidates', batchId],
    queryFn: async () => {
      const rows: DismantlingPackageDto[] = [];
      for (let page = 1; ; page += 1) {
        const { data, meta } = await fetchDismantlingPackages(batchId!, {
          processed: 'false',
          page,
          per_page: ADD_CANDIDATE_PAGE_SIZE,
        });
        rows.push(...data);
        if (data.length === 0 || page * ADD_CANDIDATE_PAGE_SIZE >= meta.total) break;
      }
      return rows.filter((pkg) => !pkg.dismantling_session_id);
    },
    enabled: !!batchId && enabled,
  });
};

export const useDismantlingSession = (id: string | undefined | null) => {
  return useQuery<DismantlingSessionDetailDto, ApiClientError>({
    queryKey: [SESSION_KEY, 'detail', id],
    queryFn: () => fetchDismantlingSession(id!),
    enabled: !!id,
  });
};

export const useCreateDismantlingSession = () => {
  const qc = useQueryClient();
  return useMutation<DismantlingSessionDetailDto, ApiClientError, CreateDismantlingSessionInput>({
    mutationFn: (body) => createDismantlingSession(body),
    onSuccess: () => invalidateDismantlingChainQueries(qc),
  });
};

export const useUpdateDismantlingSession = () => {
  const qc = useQueryClient();
  return useMutation<
    DismantlingSessionDetailDto,
    ApiClientError,
    { id: string; body: UpdateDismantlingSessionInput }
  >({
    mutationFn: ({ id, body }) => updateDismantlingSession(id, body),
    onSuccess: () => invalidateDismantlingChainQueries(qc),
  });
};

export const useAddDismantlingWorkEntry = () => {
  const qc = useQueryClient();
  return useMutation<
    DismantlingSessionDetailDto,
    ApiClientError,
    { id: string; body: AddDismantlingWorkEntryInput }
  >({
    mutationFn: ({ id, body }) => addDismantlingWorkEntry(id, body),
    onSuccess: (detail) => {
      qc.setQueryData([SESSION_KEY, 'detail', detail.dismantling_session_id], detail);
      invalidateDismantlingChainQueries(qc);
    },
  });
};

/** Void a 0 kg work entry; the row stays in the history with the reason. */
export const useVoidDismantlingWorkEntry = () => {
  const qc = useQueryClient();
  return useMutation<
    DismantlingSessionDetailDto,
    ApiClientError,
    { sessionId: string; workEntryId: string; reason: string }
  >({
    mutationFn: ({ sessionId, workEntryId, reason }) =>
      voidDismantlingWorkEntry(sessionId, workEntryId, reason),
    onSuccess: (detail) => {
      qc.setQueryData([SESSION_KEY, 'detail', detail.dismantling_session_id], detail);
      invalidateDismantlingChainQueries(qc);
    },
  });
};

export const useConfirmDismantlingSession = () => {
  const qc = useQueryClient();
  return useMutation<
    DismantlingSessionDetailDto,
    ApiClientError,
    { id: string; change_reason?: string }
  >({
    mutationFn: ({ id, change_reason }) => confirmDismantlingSession(id, change_reason),
    onSuccess: () => invalidateDismantlingChainQueries(qc),
  });
};

export const useDismantlingSessionCancelPreview = (
  id: string | undefined | null,
  enabled: boolean,
) => {
  return useQuery<DismantlingCancelPreview, ApiClientError>({
    queryKey: [SESSION_KEY, 'cancel-preview', id],
    queryFn: () => previewDismantlingSessionCancel(id!),
    enabled: enabled && !!id,
  });
};

export const useCancelDismantlingSession = () => {
  const qc = useQueryClient();
  return useMutation<DismantlingCancelResult, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => cancelDismantlingSession(id, reason),
    onSuccess: () => invalidateDismantlingChainQueries(qc),
  });
};

export const useCancelledDismantlingSessions = (
  params: { monthly_batch_id?: string; page?: number; per_page?: number } = {},
  enabled = true,
) => {
  return useQuery<CancelledDismantlingSessionListResult, ApiClientError>({
    queryKey: [SESSION_KEY, 'cancelled', params],
    queryFn: () => fetchCancelledDismantlingSessions(params),
    enabled,
    placeholderData: (prev) => prev,
  });
};

export const useRestoreCancelledDismantlingSession = () => {
  const qc = useQueryClient();
  return useMutation<DismantlingCancelResult, ApiClientError, string>({
    mutationFn: restoreCancelledDismantlingSession,
    onSuccess: () => invalidateDismantlingChainQueries(qc),
    onError: (error) => {
      if ([404, 409, 410].includes(error.status)) {
        void qc.invalidateQueries({ queryKey: [SESSION_KEY, 'cancelled'] });
      }
    },
  });
};

export const useConfirmDismantlingIntermediate = () => {
  const qc = useQueryClient();
  return useMutation<DismantlingIntermediateDto, ApiClientError, { id: string; sessionId: string }>(
    {
      mutationFn: ({ id }) => confirmDismantlingIntermediate(id),
      onSuccess: (_intermediate, variables) => {
        qc.invalidateQueries({ queryKey: [SESSION_KEY, 'detail', variables.sessionId] });
        invalidateDismantlingChainQueries(qc);
      },
    },
  );
};

export interface IntermediateBatchFailure {
  id: string;
  name: string;
  message: string;
  /** The original error, so the dialog can show it in the UI language. */
  error: unknown;
}

/**
 * Confirm many draft intermediates sequentially (each confirm mints a
 * pallet — sequential avoids write races). Unlike the per-item hook this
 * invalidates the package list ONCE after the whole batch; the caller owns
 * the single session-detail refresh. Per-item failures are collected, never
 * aborting the rest.
 */
export const useConfirmDismantlingIntermediatesBatch = () => {
  const qc = useQueryClient();
  return useMutation<
    IntermediateBatchFailure[],
    ApiClientError,
    { items: { id: string; name: string }[]; sessionId: string }
  >({
    mutationFn: async ({ items }) => {
      const failures: IntermediateBatchFailure[] = [];
      for (const item of items) {
        try {
          await confirmDismantlingIntermediate(item.id);
        } catch (err: unknown) {
          failures.push({
            id: item.id,
            name: item.name,
            message: err instanceof Error ? err.message : String(err),
            error: err,
          });
        }
      }
      return failures;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [PKG_KEY] });
      invalidateDismantlingDownstreamQueries(qc);
    },
  });
};

export const useVoidDismantlingIntermediate = () => {
  const qc = useQueryClient();
  return useMutation<
    DismantlingIntermediateDto,
    ApiClientError,
    { id: string; sessionId: string; reason: string }
  >({
    mutationFn: ({ id, reason }) => voidDismantlingIntermediate(id, reason),
    onSuccess: (_intermediate, variables) => {
      qc.invalidateQueries({ queryKey: [SESSION_KEY, 'detail', variables.sessionId] });
      invalidateDismantlingChainQueries(qc);
    },
  });
};
